/** @format */
import React, { useEffect, useState } from "react";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { useSession } from "../context/SessionContext";
import { useTranslation } from "react-i18next";
import i18n from "../i18n"; // Import i18n instance
import ErrorPopup from "../components/ErrorPopup";
import { supabase } from "../supabaseClient";
import toast from "react-hot-toast";
import CreateAgentDialog from "../components/CreateAgentDialog";
import PersonalInfoCard from "../components/Profile/PersonalInfoCard";
import LanguageCard from "../components/Profile/LanguageCard";
import AgentManagementCard from "../components/Profile/AgentManagementCard";
import ResetPasswordDialog from "../components/Profile/ResetPasswordDialog";
import { getAgentAbbreviation } from "../lib/chatUtils";

const generateSecurePassword = (length: number = 16): string => {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const symbols = "!@#$%^&*()_+-=[]{}|;:,.<>?";
  const allChars = uppercase + lowercase + numbers + symbols;

  let password = "";
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  return password.split("").sort(() => Math.random() - 0.5).join("");
};

export default function Profile() {
  const { t } = useTranslation();
  const {
    user,
    userInfo,
    loading,
    session,
    language,
    setLanguage,
    company,
    updateCompany,
    setUserInfo,
    refreshTokenIfNeeded,
    roleId,
    allAssistants,
    fetchAllUsersInfo,
  } = useSession();
  
  const [error, setError] = useState<{ message: string; code: string } | null>(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(userInfo?.name || "");

  const [editingInfo, setEditingInfo] = useState(false);
  const [tempEmail, setTempEmail] = useState<any>(user?.email || "");
  const [tempCompanyId, setTempCompanyId] = useState(company?.id || null);

  const [editingLanguage, setEditingLanguage] = useState(false);
  const [tempLang, setTempLang] = useState(language);

  const [agents, setAgents] = useState<any[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAgentEmail, setNewAgentEmail] = useState("");
  const [newAgentName, setNewAgentName] = useState("");
  const [newAgentPassword, setNewAgentPassword] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedAgentForReset, setSelectedAgentForReset] = useState<{
    email: string;
    name: string;
  } | null>(null);

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language).catch((err) => {
        console.error("Error changing language:", err);
        setError({ message: err.message, code: "OMN-001" });
      });
    }
  }, [language]);

  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const { data: listData } = await supabase.storage
          .from("avatars")
          .list(userInfo.id, { limit: 1 });

        if (listData && listData.length > 0) {
          const filePath = `${userInfo.id}/${listData[0].name}`;
          const { data } = supabase.storage
            .from("avatars")
            .getPublicUrl(filePath);
          if (data?.publicUrl) {
            setAvatarUrl(`${data.publicUrl}?t=${Date.now()}`);
          }
        }
      } catch (err: any) {
        console.error("Error fetching avatar:", err);
      }
    };
    fetchAvatar();
  }, [userInfo.id]);

  useEffect(() => {
    if (roleId === 2 && allAssistants) {
      const filteredAgents = allAssistants.filter((a: any) => {
        if (a.company_id === company?.id) return true;
        if (Array.isArray(a.companies)) {
          return a.companies.some((c: any) => c.id === company?.id);
        }
        return false;
      });

      const sortedAgents = filteredAgents.sort((a: any, b: any) => {
        if (a.id === user?.id) return -1;
        if (b.id === user?.id) return 1;
        return (a.name || "").localeCompare(b.name || "");
      });

      setAgents(sortedAgents);
    }
  }, [roleId, allAssistants, company, user?.id]);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const folder = userInfo.id;

    const { data: listData } = await supabase.storage
      .from("avatars")
      .list(folder);
    if (listData && listData.length > 0) {
      for (const file of listData) {
        await supabase.storage
          .from("avatars")
          .remove([`${folder}/${file.name}`]);
      }
    }

    const fileName = `${Date.now()}_${file.name}`;
    const filePath = `${folder}/${fileName}`;
    const { error: uploadError } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, { upsert: true });

    if (!uploadError) {
      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);
      if (urlData?.publicUrl)
        setAvatarUrl(`${urlData.publicUrl}?t=${Date.now()}`);
    }
  };

  const handleNameSave = async () => {
    if (!editedName || editedName === userInfo.name) {
      setIsEditingName(false);
      return;
    }
    try {
      await supabase
        .from("assistant")
        .update({ name: editedName })
        .eq("id", userInfo.id);
      setUserInfo({ ...userInfo, name: editedName });
    } catch (err: any) {
      setError({ message: err.message, code: "OMN-007" });
    }
    setIsEditingName(false);
  };

  const handleInfoSave = () => {
    setUserInfo({
      ...userInfo,
      email: tempEmail,
      company: userInfo.companies.find((c: any) => c.id === tempCompanyId),
    });
    updateCompany(userInfo.companies.find((c: any) => c.id === tempCompanyId));
    setEditingInfo(false);
  };

  const handleLanguageSave = async () => {
    refreshTokenIfNeeded();
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/${
          session!.user.id
        }/language`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ language: tempLang }),
        }
      );
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/callcenter/${
          session!.user.id
        }`,
        {
          headers: {
            Authorization: `${session!.token_type} ${session!.access_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await res.json();
      localStorage.setItem("preferredLanguage", data.language);
      setLanguage(data.language);
      setUserInfo(data);
    } catch (err: any) {
      setError({ message: err.message, code: "OMN-003" });
    }
    setEditingLanguage(false);
  };

  const handleSendPasswordReset = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/#/reset-password`,
      });

      if (error) throw error;

      toast.success(t("password_reset_sent"));
      setResetPasswordDialogOpen(false);
      setSelectedAgentForReset(null);
    } catch (err: any) {
      console.error("Error sending password reset:", err);
      toast.error(t("error_sending_reset"));
    }
  };

  const openResetPasswordDialog = (email: string, name: string) => {
    setSelectedAgentForReset({ email, name });
    setResetPasswordDialogOpen(true);
  };

  const handleCreateAgent = async () => {
    if (!newAgentEmail || !newAgentName || !newAgentPassword) {
      toast.error(t("fill_all_fields"));
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: newAgentEmail,
        password: newAgentPassword,
      });

      if (error) throw error;

      if (data.user) {
        await supabase.from("assistant").insert({
          id: data.user.id,
          name: newAgentName,
          email: newAgentEmail,
          company_id: company?.id,
          role_id: 3,
          must_change_password: true,
        });

        toast.success(t("agent_created_successfully"));
        setCreateDialogOpen(false);
        setNewAgentEmail("");
        setNewAgentName("");
        setNewAgentPassword("");

        if (session) {
          await fetchAllUsersInfo(session);
        }
      }
    } catch (err: any) {
      console.error("Error creating agent:", err);
      toast.error(err.message || t("error_creating_agent"));
    } finally {
      setIsCreating(false);
    }
  };

  const getRoleName = (roleId: number) => {
    switch (roleId) {
      case 1:
        return t("super_admin");
      case 2:
        return t("admin");
      case 3:
        return t("agent");
      default:
        return t("unknown");
    }
  };

  if (loading) return <div>{t("loading")}</div>;
  if (!user || !userInfo) return <div>{t("no_user_data")}</div>;
  const abbreviation = getAgentAbbreviation(userInfo.name, false);
  return (
    <div className="h-screen overflow-y-auto bg-white">
      <div className="container mx-auto px-4 py-8 pb-24">
        {error && <ErrorPopup error={error} onClose={() => setError(null)} />}

        <div className="text-center mb-8 flex-shrink-0">
          <div className="flex items-center justify-center gap-2 mb-2">
            {isEditingName ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                className="text-lg font-semibold text-center border-b outline-none"
                autoFocus
              />
            ) : (
              <h1 className="text-base sm:text-lg md:text-xl font-semibold">
                {userInfo.name}
              </h1>
            )}
          </div>

                <div className="relative w-[74px] h-[74px] mx-auto mt-2">
                  <Avatar className="w-20 h-20 sm:w-[74px] sm:h-[74px]">
                    <AvatarFallback className="w-[74px] h-[74px] flex items-center justify-center rounded-full bg-[#105BDB] text-white font-semibold text-3xl uppercase">
                      {abbreviation}
                    </AvatarFallback>
                  </Avatar>

            <label
              htmlFor="avatar-upload"
              className="absolute -bottom-2 -right-2 cursor-pointer"
            >
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
              />
            </label>
          </div>
        </div>

        {roleId === 2 ? (
          <div className="max-w-7xl mx-auto space-y-6 flex-1 flex flex-col min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <PersonalInfoCard
                email={user.email || ""}
                companyName={company.name}
                editingInfo={editingInfo}
                tempEmail={tempEmail}
                tempCompanyId={tempCompanyId}
                companies={userInfo.companies}
                onEditClick={() => setEditingInfo(true)}
                onSaveClick={handleInfoSave}
                setTempEmail={setTempEmail}
                setTempCompanyId={setTempCompanyId}
                t={t}
                compact={true}
              />

              <LanguageCard
                language={language}
                editingLanguage={editingLanguage}
                tempLang={tempLang}
                onEditClick={() => setEditingLanguage(true)}
                onSaveClick={handleLanguageSave}
                setTempLang={setTempLang}
                t={t}
                compact={true}
              />
            </div>

            <div className="flex-1 min-h-0">
              <AgentManagementCard
                agents={agents}
                currentUserId={user?.id || ""}
                onCreateAgent={() => {
                  setNewAgentPassword(generateSecurePassword());
                  setCreateDialogOpen(true);
                }}
                onResetPassword={openResetPasswordDialog}
                getRoleName={getRoleName}
                t={t}
              />
            </div>
          </div>
        ) : (
          <div className="max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto space-y-6">
            <PersonalInfoCard
              email={user.email || ""}
              companyName={company.name}
              editingInfo={editingInfo}
              tempEmail={tempEmail}
              tempCompanyId={tempCompanyId}
              companies={userInfo.companies}
              onEditClick={() => setEditingInfo(true)}
              onSaveClick={handleInfoSave}
              setTempEmail={setTempEmail}
              setTempCompanyId={setTempCompanyId}
              t={t}
            />

            <LanguageCard
              language={language}
              editingLanguage={editingLanguage}
              tempLang={tempLang}
              onEditClick={() => setEditingLanguage(true)}
              onSaveClick={handleLanguageSave}
              setTempLang={setTempLang}
              t={t}
            />
          </div>
        )}
      </div>

      <CreateAgentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        newAgentEmail={newAgentEmail}
        newAgentName={newAgentName}
        newAgentPassword={newAgentPassword}
        setNewAgentEmail={setNewAgentEmail}
        setNewAgentName={setNewAgentName}
        setNewAgentPassword={setNewAgentPassword}
        onConfirm={handleCreateAgent}
        isCreating={isCreating}
        generatePassword={generateSecurePassword}
        t={t}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        selectedAgent={selectedAgentForReset}
        onConfirm={handleSendPasswordReset}
        t={t}
      />
    </div>
  );
}
